import { supabase } from "./supabase";
import { dsHttpOrigin } from "../online/net";
import { reportSignal } from "./monitoring";

// Signing in while the game runs inside another site's frame (its itch.io
// page). Google and Discord refuse to show their sign-in pages in a frame, and
// the browser keeps a framed page's storage apart from achtungroyale.io's own,
// so a sign-in finished in another tab never reaches the frame.
//
// So the frame opens /auth/popup in a small window with a secret code: a
// normal, top-level page of ours where every sign-in works. Once signed in,
// that window leaves the session with the game server under the code
// (edge-server auth/relay.js) and also posts it straight to the frame when the
// browser still lets it. The frame waits for either and stores the session in
// its own storage. The window keeps its copy in memory only (supabase.ts), so
// the two never share a refresh token.

export const POPUP_PATH = "/auth/popup";
const MESSAGE = "ar-session";
const POLL_MS = 1500;
const WAIT_MS = 15 * 60 * 1000; // a magic link can take a while to arrive

export type PopupProvider = "google" | "discord" | "facebook";

export const inFrame = (() => {
  if (typeof window === "undefined") return false;
  try { return window.self !== window.top; } catch { return true; }
})();

export const isAuthPopup = typeof window !== "undefined" && window.location.pathname === POPUP_PATH;

function newCode(): string {
  const b = new Uint8Array(32);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
}

// The frame: open the sign-in window (call it inside the click, or the browser
// blocks it) and wait for the session. `provider` starts that sign-in at once;
// without one the window shows the usual sign-in choices. Resolves true once
// signed in, false when given up (stop(), or nothing came in time).
export function startFramedSignIn(provider?: PopupProvider): { opened: boolean; done: Promise<{ ok: boolean; error?: string }>; stop: () => void } {
  const code = newCode();
  const w = 480, h = 700;
  const left = Math.max(0, (window.screenX || 0) + (window.outerWidth - w) / 2);
  const top = Math.max(0, (window.screenY || 0) + (window.outerHeight - h) / 2);
  const url = `${window.location.origin}${POPUP_PATH}?relay=${code}${provider ? `&p=${provider}` : ""}`;
  const popup = window.open(url, "ar-signin", `width=${w},height=${h},left=${left},top=${top}`);

  let stopped = false;
  (window as any).__arSignInPending = true;
  let finish: (r: { ok: boolean; error?: string }) => void = () => {};
  const done = new Promise<{ ok: boolean; error?: string }>((resolve) => { finish = resolve; });
  const adopt = async (t: { access_token?: unknown; refresh_token?: unknown }, via: string) => {
    if (stopped || !supabase) return;
    if (typeof t.access_token !== "string" || typeof t.refresh_token !== "string") {
      reportSignal("framed sign-in: bad session", { via, keys: Object.keys(t || {}).join(",") });
      return;
    }
    stopped = true;
    cleanup();
    try {
      const { error } = await supabase.auth.setSession({ access_token: t.access_token, refresh_token: t.refresh_token });
      if (error) reportSignal("framed sign-in: setSession failed", { via, message: error.message, status: (error as any).status ?? null, code: (error as any).code ?? null });
      finish(error ? { ok: false, error: error.message } : { ok: true });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      reportSignal("framed sign-in: setSession threw", { via, message });
      finish({ ok: false, error: message });
    }
  };
  const onMessage = (e: MessageEvent) => {
    if (e.origin === window.location.origin && e.data?.type === MESSAGE && e.data.code === code) void adopt(e.data, "message");
  };
  window.addEventListener("message", onMessage);
  const started = Date.now();
  let pollErrors = 0;
  const poll = window.setInterval(async () => {
    if (stopped) return;
    if (Date.now() - started > WAIT_MS) { stop(); return; }
    try {
      const res = await fetch(`${dsHttpOrigin()}/auth/relay?code=${code}`, { cache: "no-store" });
      if (res.ok) void adopt(await res.json(), "server");
      else if (res.status !== 404) reportSignal("framed sign-in: relay poll", { status: res.status });
    } catch (e) { pollErrors++; if (pollErrors === 3) reportSignal("framed sign-in: relay poll failing", { message: e instanceof Error ? e.message : String(e) }); }
  }, POLL_MS);
  function cleanup() {
    (window as any).__arSignInPending = false;
    window.clearInterval(poll);
    window.removeEventListener("message", onMessage);
  }
  function stop() {
    if (stopped) return;
    stopped = true;
    cleanup();
    finish({ ok: false });
  }
  return { opened: !!popup, done, stop };
}

// The sign-in window: hand the session to the frame that asked for it.
// True when the server took it (the frame then has it within a poll).
export async function deliverSession(code: string, session: { access_token: string; refresh_token: string }): Promise<boolean> {
  const tokens = { access_token: session.access_token, refresh_token: session.refresh_token };
  try {
    const opener = window.opener as Window | null;
    if (opener && !opener.closed) opener.postMessage({ type: MESSAGE, code, ...tokens }, window.location.origin);
  } catch { /* the browser cut the link to the frame: the server still has it */ }
  let last = "";
  for (let i = 0; i < 3; i++) {
    try {
      const res = await fetch(`${dsHttpOrigin()}/auth/relay`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ code, ...tokens }) });
      if (res.ok) return true;
      last = "status " + res.status;
    } catch (e) { last = e instanceof Error ? e.message : String(e); }
    await new Promise((r) => setTimeout(r, 800));
  }
  reportSignal("framed sign-in: hand-over failed", { last });
  return false;
}
